import { create } from 'zustand';

import type { USBDriveData } from '@sb/feathers-types';
import { makeNamespacedLog } from '@sb/log';
import { wait } from '@sb/utilities';
import { getUSBDrives } from '@sbrc/services/feathers-client';

const log = makeNamespacedLog('useUSBDrivesWithDockerImages');

interface USBDockerImagesState {
  usbDrives: USBDriveData[];
  error: string | null;
}

const INITIAL_STATE: USBDockerImagesState = {
  usbDrives: [],
  error: null,
};

const useUSBDockerImagesStore = create<USBDockerImagesState>(
  () => INITIAL_STATE,
);

// Generate a cache string for USB drives to enable fast comparison
const generateUSBCacheString = (drives: USBDriveData[]): string => {
  return drives
    .map((drive) => `${drive.label}:${drive.dockerImages.length}`)
    .join('|');
};

export const pollUSBDrives = async (): Promise<void> => {
  let driveDataCache = '';

  while (true) {
    try {
      const usbDrives = await getUSBDrives('dockerImages');

      const newCacheString = generateUSBCacheString(usbDrives);

      // Only update state/rerender if the drives have actually changed
      if (newCacheString !== driveDataCache) {
        useUSBDockerImagesStore.setState({
          usbDrives,
          error: null,
        });

        driveDataCache = newCacheString;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      log.warn('poll.fail', 'Failed to get USB docker images', message);

      useUSBDockerImagesStore.setState({
        error: message,
      });
    }

    await wait(3_000);
  }
};

// gets usb drives, including docker image data
export const useUSBDrivesWithDockerImages = (): USBDriveData[] => {
  const { usbDrives } = useUSBDockerImagesStore();

  return usbDrives;
};
